// ============================================================
// HARMONIC CHARTER — Chord Name Parser
// ============================================================

import type {
  Chord,
  ChordQuality,
  Extension,
  NoteRoot,
  Key,
  ParseResult,
  NormalizedAccidental,
} from '../types';
import { noteToDegreee } from '../theory';

const NOTE_TOKENS: string[] = [
  'C#', 'Cb',
  'Db', 'D#',
  'Eb', 'E#',
  'Fb', 'F#',
  'Gb', 'G#',
  'Ab', 'A#',
  'Bb', 'B#',
  'C', 'D', 'E', 'F', 'G', 'A', 'B',
];

const QUALITY_SUFFIXES: Array<[string, ChordQuality]> = [
  ['mMaj7',  'minorMajor7'],
  ['m7b5',   'halfDiminished'],
  ['maj7',   'major7'],
  ['min7',   'minor7'],
  ['m7',     'minor7'],
  ['dim7',   'diminished'],
  ['dim',    'diminished'],
  ['aug',    'augmented'],
  ['sus4',   'sus4'],
  ['sus2',   'sus2'],
  ['maj9',   'major9'],
  ['m9',     'minor9'],
  ['9',      'dominant9'],
  ['m',      'minor'],
  ['7',      'dominant7'],
];

const EXTENSION_TOKENS: Array<[string, Extension]> = [
  ['add9',  'add9'],
  ['add11', 'add11'],
  ['add13', 'add13'],
  ['b9',    'b9'],
  ['#9',    '#9'],
  ['#11',   '#11'],
  ['b13',   'b13'],
  ['11',    'add11'],
  ['13',    'add13'],
];

// ------------------------------------------------------------
// NORMALIZE — symbol form → word form
// ------------------------------------------------------------

function normalizeAccidental(raw: string | null | undefined): NormalizedAccidental {
  if (raw === '#') return 'sharp';
  if (raw === 'b') return 'flat';
  if (raw === 'sharp') return 'sharp';
  if (raw === 'flat') return 'flat';
  return null;
}

// ------------------------------------------------------------
// HELPERS
// ------------------------------------------------------------

function parseNoteName(input: string, pos: number): [string | null, number] {
  for (const token of NOTE_TOKENS) {
    if (input.startsWith(token, pos)) return [token, pos + token.length];
  }
  return [null, pos];
}

function parseQualitySuffix(input: string, pos: number): [ChordQuality, number] {
  for (const [token, quality] of QUALITY_SUFFIXES) {
    if (input.startsWith(token, pos)) return [quality, pos + token.length];
  }
  return ['major', pos];
}

function parseExtensions(input: string, pos: number): [Extension[], number] {
  const extensions: Extension[] = [];
  let current = pos;
  while (current < input.length) {
    let matched = false;
    for (const [token, ext] of EXTENSION_TOKENS) {
      if (input.startsWith(token, current)) {
        extensions.push(ext);
        current += token.length;
        matched = true;
        break;
      }
    }
    if (!matched) break;
  }
  return [extensions, current];
}

function parseSlashNote(
  input: string,
  pos: number,
  key: Key,
): [string | null, number, string | null] {
  if (input[pos] !== '/') return [null, pos, null];

  const current = pos + 1;
  const [note, afterNote] = parseNoteName(input, current);
  if (note === null) return [null, pos, `Expected note name after '/'`];

  const resolved = noteToDegreee(note as NoteRoot, key);
  if (resolved === null) {
    return [null, pos, `Cannot resolve bass note '${note}' in key ${key.root} ${key.scale}`];
  }

  // Slash is stored as a plain string for Nashville/Roman rendering,
  // but we embed degree+accidental as structured data for chord-name mode.
  // Use a JSON-encoded string so slash stays typed as string | null.
  const slash: string = JSON.stringify({
    degree: resolved.degree,
    accidental: normalizeAccidental((resolved as any).accidental),
  });

  return [slash, afterNote, null];
}

// ------------------------------------------------------------
// MAIN EXPORT
// ------------------------------------------------------------

export function parseChordName(token: string, key: Key): ParseResult {
  const input = token.trim();
  if (input === '') return { ok: false, error: 'Empty token', raw: token };

  let pos = 0;

  // 1. Root note name
  const [rootNote, afterNote] = parseNoteName(input, pos);
  if (rootNote === null) {
    return {
      ok: false,
      error: `Expected note name (C, F#, Bb...), got '${input[pos]}'`,
      raw: token,
    };
  }
  pos = afterNote;

  // 2. Resolve to degree + accidental (normalize symbol → word form)
  const resolved = noteToDegreee(rootNote as NoteRoot, key);
  if (resolved === null) {
    return {
      ok: false,
      error: `Cannot resolve '${rootNote}' to a scale degree in key ${key.root} ${key.scale}`,
      raw: token,
    };
  }
  const degree = resolved.degree;
  const accidental: NormalizedAccidental = normalizeAccidental((resolved as any).accidental);

  // 3. Quality suffix
  const [quality, afterQuality] = parseQualitySuffix(input, pos);
  pos = afterQuality;

  // 4. Extensions
  const [extensions, afterExt] = parseExtensions(input, pos);
  pos = afterExt;

  // 5. Slash chord
  const [slash, afterSlash, slashError] = parseSlashNote(input, pos, key);
  if (slashError !== null) return { ok: false, error: slashError, raw: token };
  pos = afterSlash;

  // 6. Trailing garbage
  if (pos < input.length) {
    return {
      ok: false,
      error: `Unexpected characters '${input.slice(pos)}' after chord`,
      raw: token,
    };
  }

  const chord: Chord = {
    degree,
    accidental,
    quality,
    extensions,
    slash,
    position: 0,
    borrowed: false,
  };

  return { ok: true, chord };
}

export function looksLikeChordName(token: string): boolean {
  return /^[A-G]/.test(token.trim());
}